'use client'

import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface TextRotateProps {
  texts: string[]
  rotationInterval?: number
  className?: string
  staggerDuration?: number
}

export function TextRotate({
  texts,
  rotationInterval = 2000,
  className,
  staggerDuration = 0.025,
}: TextRotateProps) {
  const [index, setIndex] = useState(0)
  const [hasMounted, setHasMounted] = useState(false)
  const shouldReduceMotion = useReducedMotion()
  const longestText = useMemo(() => {
    if (texts.length === 0) return ''
    return texts.reduce((a, b) => (a.length > b.length ? a : b))
  }, [texts])

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setHasMounted(true)
    })
    return () => cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    if (texts.length <= 1 || shouldReduceMotion || !hasMounted) return
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % texts.length)
    }, rotationInterval)
    return () => clearInterval(interval)
  }, [texts, rotationInterval, shouldReduceMotion, hasMounted])

  const safeIndex = shouldReduceMotion ? 0 : index
  const currentText = texts[safeIndex] ?? ''
  const currentChars = useMemo(() => currentText.split(''), [currentText])
  const shouldAnimate = hasMounted && !shouldReduceMotion && texts.length > 1

  if (texts.length === 0) return null

  return (
    <div
      className={cn('relative inline-flex items-center justify-center overflow-visible', className)}
    >
      {/* Invisible placeholder to reserve width/height based on longest word. 
          Must be relative/static to give the container size. */}
      <span
        className={cn('invisible whitespace-nowrap px-2 opacity-0', className)}
        aria-hidden="true"
      >
        {longestText}
      </span>

      {!shouldAnimate ? (
        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center whitespace-nowrap',
            className
          )}
          aria-label={currentText}
        >
          {currentText}
        </span>
      ) : (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={safeIndex}
            className="absolute inset-0 flex items-center justify-center whitespace-nowrap"
            initial="initial"
            animate="animate"
            exit="exit"
            aria-label={currentText}
          >
            {currentChars.map((char, i) => {
              const charCount = currentText.length
              const backgroundPosition = `${(i / (charCount - 1 || 1)) * 100}% 50%`

              return (
                <motion.span
                  key={`${currentText}-${i}`}
                  variants={{
                    initial: {
                      y: '100%',
                      opacity: 0,
                      scale: 1.05,
                    },
                    animate: {
                      y: 0,
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: 0.3, // Much faster
                        ease: [0.2, 0, 0, 1], // "Extreme" ease out (snappy)
                        delay: i * staggerDuration,
                      },
                    },
                    exit: {
                      y: '-100%',
                      opacity: 0,
                      scale: 0.98,
                      transition: {
                        duration: 0.25,
                        ease: 'easeIn',
                        delay: i * staggerDuration,
                      },
                    },
                  }}
                  style={{
                    backgroundPosition,
                    backgroundSize: `${charCount * 100}% 100%`,
                  }}
                  className={cn(
                    'inline-block px-[0.02em]', // Slight horizontal spacing to avoid clipping
                    className
                  )}
                >
                  {char}
                </motion.span>
              )
            })}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  )
}
